import React from "react";
import { connect } from "react-redux";
import {
    Box,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { ApplicationState } from "../../redux/reducers";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IGuiaCreateResult } from "../../entities/guias/IGuiaCreateResult";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { Mercancia } from "../../entities/solicitudes/Mercancia";
import { MercanciaDatosComplementarios } from "../../entities/solicitudes/MercanciaDatosComplementarios";
import { IGuiaTarifa, IGuiaTarifaMercancia } from "../../entities/guias/IGuiaTarifa";
import { consolidarServiciosGuia } from "../servicioAdicional/consolidarServiciosGuiaHelpers";
import { currencyFormat } from "../../blocks/utility";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import {
    filaComplementarioResumen,
    fletePresentacionCliente,
    costoServicioAdicionalVisual,
    nombreServicioAdicional,
    nombreUnidadPorId,
    sumaServiciosAdicionalesVisual,
    textoConUnidad
} from "./revisionGuiaResumenHelpers";
import { ResumenRecoleccionReadOnly, ResumenSeguroReadOnly } from "./revisionGuiaReadOnlyCards";
import { IParametro } from "../../redux/types/parametroActionTypes";
import { esServicioRetornoDocumentos } from "../servicioAdicional/retornoDocumentosGuiaHelpers";

const useStyles = makeStyles(() =>
    createStyles({
        root: { padding: "16px 24px 24px" },
        pageTitle: {
            color: "#19754e",
            fontWeight: 600,
            marginBottom: 8
        },
        guiaNumero: {
            color: "#000",
            textDecoration: "underline",
            fontWeight: 700
        },
        sectionTitle: {
            color: "#19754e",
            fontSize: "14px",
            fontWeight: 700,
            marginTop: "1.25em",
            marginBottom: "0.5em"
        },
        subsectionTitle: {
            color: "#19754e",
            fontSize: "13px",
            fontWeight: 700,
            marginTop: 8,
            marginBottom: 4
        },
        documentoItem: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.87)",
            lineHeight: 1.5
        },
        sectionBlock: {
            marginBottom: "0.25em"
        },
        blockText: {
            fontSize: "14px",
            color: "rgba(0,0,0,0.87)",
            lineHeight: 1.5
        },
        tableSummary: {
            width: "100%",
            fontSize: "14px",
            "& td": { padding: "6px 0", verticalAlign: "top" },
            "& td.price": { textAlign: "right" as const, whiteSpace: "nowrap" as const }
        },
        tableSummaryRow: {
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
        },
        totalBox: {
            backgroundColor: "#338462",
            color: "#fff",
            marginTop: "1em",
            padding: "16px 20px",
            borderRadius: 4
        },
        totalRow: { fontWeight: 700, fontSize: "18px" },
        mercanciaTable: {
            "& .MuiTableCell-root": {
                fontSize: "13px",
                padding: "6px 8px"
            }
        }
    })
);

const sumValor = (items: Array<{ valor?: number | null }> | undefined): number =>
    (items ?? []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0);

const sumCombustible = (items: IGuiaTarifa["combustible"]): number =>
    (items ?? []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0);

const textoCpCiudad = (rec: IRecoleccion): string => {
    const cp = rec?.codigoPostal || rec?.colonia?.codigoPostal || "";
    const ciudad = rec?.sucursal?.ciudad?.nombre || rec?.colonia?.ciudad?.nombre || "";
    const estado = rec?.sucursal?.estado?.nombre || rec?.colonia?.estado?.nombre || "";
    return [cp, ciudad, estado].filter(Boolean).join(" ");
};

const nombreEmbalaje = (m: Mercancia): string => {
    if (localStorage.getItem("idioma") === "en-US") {
        return m.etiquetaEmbalaje?.nombre ?? m.embalaje?.nombre ?? String(m.embalaje ?? "");
    }
    return m.embalaje?.nombre ?? String(m.embalaje ?? "");
};

const columnSpan = (paired: boolean): 6 | 12 => (paired ? 6 : 12);

interface StateProps {
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    guiaMercancias: Mercancia[];
    guiaMercanciasComplementarias: MercanciaDatosComplementarios[];
    guiaServicios: ServicioAdicional[];
    guiaTarifa: IGuiaTarifa;
    parametros: IParametro[];
}

interface OwnProps {
    titulo: string;
    createResult?: IGuiaCreateResult | null;
    mensajeExito?: string;
    ocultarEncabezadoSuperior?: boolean;
    soloTotalGeneral?: boolean;
}

type Props = StateProps & OwnProps;

const RevisionGuiaResumenView = ({
    titulo,
    createResult,
    mensajeExito,
    ocultarEncabezadoSuperior = false,
    soloTotalGeneral = false,
    guiaWizard,
    guiaOrigen,
    guiaDestino,
    guiaMercancias,
    guiaMercanciasComplementarias,
    guiaServicios,
    guiaTarifa,
    parametros
}: Props) => {
    const classes = useStyles();
    const consolidado = consolidarServiciosGuia(
        guiaOrigen?.serviciosAdicionales ?? [],
        guiaDestino?.serviciosAdicionales ?? [],
        guiaServicios ?? []
    );
    const serviciosSeleccionados = consolidado.servicios.filter(s => s.seleccionado);
    const totalesTarifa = guiaTarifa.totales;
    const totales = createResult
        ? {
            importe: totalesTarifa?.importe ?? 0,
            subtotal: createResult.subTotal,
            iva: createResult.iva,
            retencion: createResult.retencion,
            total: createResult.total,
            paridad: totalesTarifa?.paridad ?? 0,
            descuento: totalesTarifa?.descuento ?? 0
        }
        : totalesTarifa;

    const mercanciasValidas = guiaMercancias.filter(m => (m.cantidad || 0) > 0 && (m.embalajeId || 0) > 0);
    type FilaMercancia = { tarifa: IGuiaTarifaMercancia | null; captura: Mercancia };
    const filasMercancia: FilaMercancia[] = guiaTarifa.mercancias?.length
        ? guiaTarifa.mercancias.map((tm, index) => {
            const captura = mercanciasValidas[index]
                ?? mercanciasValidas.find(m => m.embalajeId === tm.embalajeId)
                ?? mercanciasValidas[0];
            return { tarifa: tm, captura };
        })
        : mercanciasValidas.map(captura => ({ tarifa: null, captura }));

    const flete = sumValor(guiaTarifa.desgloseFlete);
    const rad = sumValor(guiaTarifa.desgloseRad);
    const ead = sumValor(guiaTarifa.desgloseEad);
    const fleteVisual = fletePresentacionCliente(flete, rad, ead);
    const ajuste = sumValor(guiaTarifa.desgloseAjuste);
    const cruce = sumValor(guiaTarifa.desgloseCruce);
    const seguro = sumValor(guiaTarifa.desgloseSeguro);
    const combustible = sumCombustible(guiaTarifa.combustible);

    const tieneDescripcionContenido = !!guiaWizard.descripcionMercancia?.trim();
    const tieneComplementarios = guiaMercanciasComplementarias.length > 0;
    const tieneSeguro = guiaWizard.esSeguroBasico || guiaWizard.esSeguroMercancia;
    const referenciasVisibles = guiaWizard.clienteReferencias?.filter(r => r.valor?.trim()) ?? [];
    const tieneReferencias = referenciasVisibles.length > 0;
    const tieneServicios = serviciosSeleccionados.length > 0;
    const totalServiciosAdicionales = sumaServiciosAdicionalesVisual(
        serviciosSeleccionados,
        guiaTarifa.conceptos
    );
    const tieneObservaciones = !!guiaWizard.observaciones?.trim();

    const unidadPeso = nombreUnidadPorId(guiaWizard.unidadMedidaPesoId);
    const unidadMedida = nombreUnidadPorId(guiaWizard.unidadMedidaLongitudId);
    const unidadVolumen = unidadMedida ? `${unidadMedida}³` : "";

    return (
        <Box className={classes.root}>
            {titulo && (
                <Typography variant="h6" className={classes.pageTitle}>
                    {titulo}
                </Typography>
            )}

            {!ocultarEncabezadoSuperior && createResult?.numGuia && (
                <Typography className={classes.blockText} style={{ marginBottom: 16 }}>
                    {traducirLabel(354)}{" "}
                    <span className={classes.guiaNumero}>{createResult.numGuia}</span>
                </Typography>
            )}

            {!ocultarEncabezadoSuperior && mensajeExito && (
                <Typography className={classes.blockText} style={{ marginBottom: 16 }}>
                    {mensajeExito}
                </Typography>
            )}

            <Box className={classes.sectionBlock}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.sectionTitle}>{traducirLabel(222)}</Typography>
                        <Typography className={classes.blockText}>
                            {guiaOrigen.distribuidor?.nombre || guiaOrigen.distribuidor?.nombreClienteDistribuidor || ""}
                        </Typography>
                        <Typography className={classes.blockText}>{textoCpCiudad(guiaOrigen)}</Typography>
                        <ResumenRecoleccionReadOnly rec={guiaOrigen} esOrigen={true} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.sectionTitle}>{traducirLabel(86)}</Typography>
                        <Typography className={classes.blockText}>
                            {guiaDestino.distribuidor?.nombre || guiaDestino.distribuidor?.nombreClienteDistribuidor || ""}
                        </Typography>
                        <Typography className={classes.blockText}>{textoCpCiudad(guiaDestino)}</Typography>
                        <ResumenRecoleccionReadOnly rec={guiaDestino} esOrigen={false} />
                    </Grid>
                </Grid>
            </Box>

            <Box className={classes.sectionBlock}>
                <Typography className={classes.sectionTitle}>{traducirLabel(88)}</Typography>
                <TableContainer>
                    <Table size="small" className={classes.mercanciaTable}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{traducirLabel(29)}</TableCell>
                                <TableCell align="center">{traducirLabel(111)}</TableCell>
                                <TableCell align="center">{traducirLabel(229)}</TableCell>
                                <TableCell align="center">{traducirLabel(174)}</TableCell>
                                <TableCell align="center">{traducirLabel(320)}</TableCell>
                                <TableCell align="center">{traducirLabel(230)}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filasMercancia.map(({ tarifa, captura }, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="center">{tarifa?.cantidad ?? captura?.cantidad}</TableCell>
                                    <TableCell align="center">{captura ? nombreEmbalaje(captura) : ""}</TableCell>
                                    <TableCell align="center">
                                        {textoConUnidad(tarifa?.peso ?? captura?.peso, unidadPeso)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {captura
                                            ? textoConUnidad(
                                                `${captura.largo ?? ""} x ${captura.ancho ?? ""} x ${captura.alto ?? ""}`,
                                                unidadMedida
                                            )
                                            : ""}
                                    </TableCell>
                                    <TableCell align="center">
                                        {textoConUnidad(tarifa?.volumen ?? captura?.volumen, unidadVolumen)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {textoConUnidad(tarifa?.pesoFacturado ?? "", unidadPeso)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {tieneComplementarios && (
                <Box className={classes.sectionBlock}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(89)}</Typography>
                    <TableContainer>
                        <Table size="small" className={classes.mercanciaTable}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">{traducirLabel(29)}</TableCell>
                                    <TableCell align="center">{traducirLabel(37)}</TableCell>
                                    <TableCell align="center">{traducirLabel(36)}</TableCell>
                                    <TableCell align="center">{traducirLabel(81)}</TableCell>
                                    <TableCell align="center">{traducirLabel(144)}</TableCell>
                                    <TableCell align="center">{traducirLabel(313)}</TableCell>
                                    <TableCell align="center">{traducirLabel(231)}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {guiaMercanciasComplementarias.map((c, i) => {
                                    const fila = filaComplementarioResumen(c);
                                    return (
                                        <TableRow key={i}>
                                            <TableCell align="center">{fila.cantidad}</TableCell>
                                            <TableCell align="center">{fila.claveUnidad}</TableCell>
                                            <TableCell align="center">{fila.claveProducto}</TableCell>
                                            <TableCell align="center">{fila.descripcion}</TableCell>
                                            <TableCell align="center">{fila.fraccionArancelaria}</TableCell>
                                            <TableCell align="center">{fila.uuidComprobante}</TableCell>
                                            <TableCell align="center">{fila.peso}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {(tieneDescripcionContenido || tieneSeguro) && (
                <Grid container spacing={3} className={classes.sectionBlock}>
                    {tieneDescripcionContenido && (
                        <Grid item xs={12} md={columnSpan(tieneSeguro)}>
                            <Typography className={classes.sectionTitle}>{traducirLabel(83)}</Typography>
                            <Typography className={classes.blockText}>{guiaWizard.descripcionMercancia}</Typography>
                        </Grid>
                    )}
                    {tieneSeguro && (
                        <Grid item xs={12} md={columnSpan(tieneDescripcionContenido)}>
                            <Typography className={classes.sectionTitle}>{traducirMessage(22)}</Typography>
                            <ResumenSeguroReadOnly
                                guiaWizard={guiaWizard}
                                parametros={parametros ?? []}
                                costoTarifa={seguro}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {(tieneReferencias || tieneObservaciones) && (
                <Grid container spacing={3} className={classes.sectionBlock}>
                    {tieneReferencias && (
                        <Grid item xs={12} md={columnSpan(tieneObservaciones)}>
                            <Typography className={classes.sectionTitle}>{traducirLabel(260)}</Typography>
                            {referenciasVisibles.map((r, i) => (
                                <Typography key={i} className={classes.blockText}>
                                    {r.descripcion}: {r.valor}
                                </Typography>
                            ))}
                        </Grid>
                    )}
                    {tieneObservaciones && (
                        <Grid item xs={12} md={columnSpan(tieneReferencias)}>
                            <Typography className={classes.sectionTitle}>{traducirMessage(27)}</Typography>
                            <Typography className={classes.blockText}>{guiaWizard.observaciones}</Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {tieneServicios && (
                <Box className={classes.sectionBlock}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(278)}</Typography>
                    <table className={classes.tableSummary}>
                        <tbody>
                            {serviciosSeleccionados.map(s => {
                                const costo = costoServicioAdicionalVisual(s, guiaTarifa.conceptos);
                                const esRetorno = esServicioRetornoDocumentos(s);
                                const documentosRetorno = esRetorno ? consolidado.documentosRetorno : [];
                                return (
                                    <React.Fragment key={s.id}>
                                        <tr className={classes.tableSummaryRow}>
                                            <td>{nombreServicioAdicional(s)}</td>
                                            {!soloTotalGeneral && (
                                                <td className="price">{currencyFormat(costo)}</td>
                                            )}
                                        </tr>
                                        {esRetorno && documentosRetorno.length > 0 && (
                                            <tr>
                                                <td colSpan={soloTotalGeneral ? 1 : 2} style={{ paddingBottom: 8 }}>
                                                    <Typography className={classes.subsectionTitle}>
                                                        {traducirLabel(102)}
                                                    </Typography>
                                                    {documentosRetorno.map((d, i) => (
                                                        <Typography key={i} className={classes.documentoItem}>
                                                            {d.descripcion?.trim() || traducirLabel(4)}
                                                        </Typography>
                                                    ))}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>
            )}

            {soloTotalGeneral ? (
                <Box className={classes.sectionBlock}>
                    <Box className={classes.totalBox}>
                        <table className={classes.tableSummary}>
                            <tbody>
                                <tr className={classes.totalRow}>
                                    <td>{traducirLabel(305)}</td>
                                    <td className="price">{currencyFormat(totales?.total ?? 0)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Box>
            ) : (
                <Grid container spacing={3} className={classes.sectionBlock}>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.sectionTitle}>{traducirLabel(366)}</Typography>
                        <table className={classes.tableSummary}>
                            <tbody>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(141)}</td><td className="price">{currencyFormat(fleteVisual)}</td></tr>
                                {tieneSeguro && (
                                    <tr className={classes.tableSummaryRow}>
                                        <td>
                                            {guiaWizard.esSeguroBasico
                                                ? traducirLabel(273)
                                                : traducirLabel(274)}
                                        </td>
                                        <td className="price">{currencyFormat(seguro)}</td>
                                    </tr>
                                )}
                                {ajuste !== 0 && (
                                    <tr className={classes.tableSummaryRow}><td>{traducirLabel(368)}</td><td className="price">{currencyFormat(ajuste)}</td></tr>
                                )}
                                {cruce !== 0 && (
                                    <tr className={classes.tableSummaryRow}><td>{traducirLabel(344)}</td><td className="price">{currencyFormat(cruce)}</td></tr>
                                )}
                                {tieneServicios && (
                                    <tr className={classes.tableSummaryRow}>
                                        <td>{traducirLabel(278)}</td>
                                        <td className="price">{currencyFormat(totalServiciosAdicionales)}</td>
                                    </tr>
                                )}
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(9)}</td><td className="price">{currencyFormat(combustible)}</td></tr>
                            </tbody>
                        </table>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.sectionTitle}>{traducirLabel(367)}</Typography>
                        <table className={classes.tableSummary}>
                            <tbody>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(363)}</td><td className="price">{currencyFormat(totales?.importe ?? 0)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(364)}</td><td className="price">{currencyFormat(totales?.paridad ?? 0)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(365)}</td><td className="price">{currencyFormat(totales?.descuento ?? 0)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(9)}</td><td className="price">{currencyFormat(combustible)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(287)}</td><td className="price">{currencyFormat(totales?.subtotal ?? 0)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(166)}</td><td className="price">{currencyFormat(totales?.iva ?? 0)}</td></tr>
                                <tr className={classes.tableSummaryRow}><td>{traducirLabel(267)}</td><td className="price">{currencyFormat(totales?.retencion ?? 0)}</td></tr>
                            </tbody>
                        </table>
                        <Box className={classes.totalBox}>
                            <table className={classes.tableSummary}>
                                <tbody>
                                    <tr className={classes.totalRow}>
                                        <td>{traducirLabel(305)}</td>
                                        <td className="price">{currencyFormat(totales?.total ?? 0)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    guiaWizard: state.guiaWizard,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    guiaMercancias: state.guiaMercancias,
    guiaMercanciasComplementarias: state.guiaMercanciasComplementarias,
    guiaServicios: state.guiaServicios,
    guiaTarifa: state.guiaTarifa,
    parametros: state.parametros
});

export default connect(mapStateToProps)(RevisionGuiaResumenView);
